import type { FileMetadataMap } from '@Core/lib/tasks/utils/fileTypes';
import { randomUuid } from '@Pages/pageRuntime/lib/randomUuid';
import type { ResourceCollectionResult } from '@Pages/pageRuntime/sdk/types';

export const EMPTY_FILES: FileMetadataMap = {};

const INITIAL_OWNER = 'initial';

// Imperative list/search/searchAll results have no unmount signal, so the store
// keeps their metadata for the page session. Each distinct parameter set mints
// its own owner key, and publish() re-merges every retained owner, so bound how
// many of those keys a session can accumulate.
const MAX_IMPERATIVE_OWNERS = 32;
const MAX_RETAINED_FILE_ENTRIES = 10_000;

export type FileMetadataSnapshot = { files: FileMetadataMap; revision: number };
export type RuntimeCollectionResult = ResourceCollectionResult & { fileMetadataRevision?: number };

export function createFileMetadataStore(initialFiles: FileMetadataMap = EMPTY_FILES) {
  const scope = randomUuid();
  const owners = new Map<object | string, FileMetadataSnapshot[]>();
  const listeners = new Set<() => void>();
  let revision = 0;
  let active = true;
  let files = initialFiles;
  let imperativeOwners: string[] = [];
  owners.set(INITIAL_OWNER, [{ files: initialFiles, revision }]);

  function forgetImperativeOwner(owner: object | string) {
    if (typeof owner === 'string') {
      imperativeOwners = imperativeOwners.filter((key) => key !== owner);
    }
  }

  function trackImperativeOwner(owner: object | string) {
    if (typeof owner !== 'string' || owner === INITIAL_OWNER) {
      return;
    }
    forgetImperativeOwner(owner);
    imperativeOwners = [...imperativeOwners, owner];
  }

  function evictImperativeOwners() {
    const entryCounts = new Map<object | string, number>();
    let retainedEntries = 0;
    let pinnedEntries = 0;
    for (const [owner, snapshots] of owners) {
      const count = snapshots.reduce(
        (total, snapshot) => total + Object.keys(snapshot.files).length,
        0
      );
      entryCounts.set(owner, count);
      retainedEntries += count;
      if (typeof owner !== 'string' || owner === INITIAL_OWNER) {
        pinnedEntries += count;
      }
    }

    const availableEntries = Math.max(0, MAX_RETAINED_FILE_ENTRIES - pinnedEntries);
    for (const owner of imperativeOwners) {
      const count = entryCounts.get(owner) ?? 0;
      if (count > availableEntries) {
        owners.delete(owner);
        forgetImperativeOwner(owner);
        retainedEntries -= count;
      }
    }

    // Initial props and mounted hooks must keep their metadata even if they
    // exceed the budget. Only imperative results have no consumer lifetime.
    while (
      imperativeOwners.length > 0 &&
      (imperativeOwners.length > MAX_IMPERATIVE_OWNERS ||
        retainedEntries > MAX_RETAINED_FILE_ENTRIES)
    ) {
      const [evicted, ...retained] = imperativeOwners;
      imperativeOwners = retained;
      owners.delete(evicted);
      retainedEntries -= entryCounts.get(evicted) ?? 0;
    }
  }

  function publish() {
    evictImperativeOwners();
    const next: FileMetadataMap = {};
    const snapshots = [...owners.values()].flat().toSorted((a, b) => a.revision - b.revision);
    for (const snapshot of snapshots) {
      Object.assign(next, snapshot.files);
    }
    if (
      Object.keys(next).length === Object.keys(files).length &&
      Object.entries(next).every(([id, file]) => files[id] === file)
    ) {
      return;
    }
    files = next;
    for (const listener of listeners) {
      listener();
    }
  }

  return {
    scope,
    getSnapshot: () => files,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    receive(result: ResourceCollectionResult): RuntimeCollectionResult {
      return { ...result, fileMetadataRevision: ++revision };
    },
    replace(owner: object | string, snapshots: FileMetadataSnapshot[]) {
      if (!active) {
        return;
      }
      owners.set(owner, snapshots);
      trackImperativeOwner(owner);
      publish();
    },
    remove(owner: object | string) {
      owners.delete(owner);
      forgetImperativeOwner(owner);
      publish();
    },
    register(owner: string, result: ResourceCollectionResult) {
      if (!active) {
        return result;
      }
      owners.set(owner, [{ files: result.files ?? EMPTY_FILES, revision: ++revision }]);
      trackImperativeOwner(owner);
      publish();
      return result;
    },
    activate() {
      active = true;
    },
    deactivate() {
      active = false;
    }
  };
}

export type FileMetadataStore = ReturnType<typeof createFileMetadataStore>;
